import type { PlanGroup, PlanItem } from "../../entities/ledger/types";
import { money } from "../../shared/lib/format";
import { ChipGroup } from "../../shared/ui/ChipGroup";
import { Select } from "../../shared/ui/Select";

export type TargetKind = "item" | "group";

export interface PlanningTarget {
  kind: TargetKind;
  id: string;
}

interface PlanningTargetPickerProps {
  items: PlanItem[];
  groups: PlanGroup[];
  value: PlanningTarget;
  onChange: (target: PlanningTarget) => void;
  // Items already filed in a group can be offered or hidden; the group screen
  // hides them, the journal offers them.
  label?: string;
}

// A transaction has one planning owner, so the kind is a choice between two
// and the id is a single select. There is deliberately no way to pick both,
// and no multi-select: the shape of the control is the shape of the rule.
export function PlanningTargetPicker({ items, groups, value, onChange, label = "Planning target" }: PlanningTargetPickerProps) {
  const options = value.kind === "group"
    ? groups.map((group) => ({ value: group.id, label: group.name }))
    : items.map((item) => ({
      value: item.id,
      label: item.name,
      hint: money(item.amount.minor, item.amount.currency),
    }));

  return (
    <>
      <div className="field">
        <span className="field-label">Assign to</span>
        <ChipGroup
          label={label} value={value.kind}
          onChange={(kind) => onChange({ kind: kind as TargetKind, id: "" })}
          options={[
            { value: "item", label: "A plan item" },
            { value: "group", label: "A plan group" },
          ]}
        />
      </div>
      <div className="field">
        <span className="field-label" id="planning-target-id">
          {value.kind === "group" ? "Group" : "Plan item"}
        </span>
        <Select
          labelledBy="planning-target-id" value={value.id}
          onChange={(id) => onChange({ ...value, id })}
          placeholder={options.length === 0
            ? (value.kind === "group" ? "No groups yet" : "No plan items in this period")
            : (value.kind === "group" ? "Choose a group" : "Choose a plan item")}
          options={options}
        />
      </div>
      <p className="panel-note">
        {value.kind === "group"
          ? "The transaction belongs to the whole composite. It is not spread over the group's items, and none of them is marked fulfilled by it."
          : "The transaction belongs to this plan item in full. There is no amount to split."}
      </p>
    </>
  );
}
